import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { ACTIONS, I18N } from 'constants/props'
import { fetchDismissNotification } from 'app_modules/notifications/actions/fetchDismissNotification'
import { fetchAddWorkingCircleMember } from 'app_modules/profile/actions/fetchAddWorkingCircleMember'
import { selectWorkingCircleNotifications, selectIsDismissing } from 'app_modules/notifications/selectors'
import actionTypes from 'app_modules/notifications/constants'
import * as sessionSelectors from 'app_modules/session/selectors'
import Translation from 'generics/Translation'

import styles from './WorkingCircleNotificationDropdown.scss'

// localStorage key scoped per profile so dismissals don't bleed across accounts on shared devices.
const dismissedKey = profileId => `dismissed_overlaps_${profileId}`

const WorkingCircleNotificationDropdown = ({
  i18n,
  isDismissing,
  items,
  notificationActions: actions,
  onClose,
  profileId,
  dispatch,
}) => {
  const t = i18n.workingCircleNotificationDropdown
  const dropdownRef = useRef(null)

  // Social overlap dismissals — stored in localStorage (FE-only, Phase 1).
  // Phase 2 option: persist server-side via dismissed_overlap_profile_ids column on profiles.
  const [dismissedOverlapIds, setDismissedOverlapIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(dismissedKey(profileId))) || []
    } catch {
      return []
    }
  })

  // Filter out overlap items the user has already dismissed
  const visibleItems = items.filter(item => item.type !== 'social_overlap' || !dismissedOverlapIds.includes(item.id))

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddBack = item => {
    // Add to working circle and dismiss the notification.
    // No navigation — user can click the avatar in My Working Circle to view the pair report.
    actions.fetchAddWorkingCircleMember(item.id, () => {
      actions.fetchDismissNotification(item.connectionId)
    })
    onClose()
  }

  const handleDismiss = connectionId => {
    actions.fetchDismissNotification(connectionId)
  }

  const handleDismissOverlap = id => {
    const updated = [...dismissedOverlapIds, id]
    setDismissedOverlapIds(updated)
    try {
      localStorage.setItem(dismissedKey(profileId), JSON.stringify(updated))
    } catch {
      // localStorage unavailable (e.g. private browsing quota exceeded) — dismiss is in-memory only
    }
    // Dispatch so Redux items update immediately — badge re-evaluates via mapStateToProps
    dispatch({ type: actionTypes.DISMISS_SOCIAL_OVERLAP_NOTIFICATION, payload: { id } })
  }

  const renderAvatar = item => {
    if (item.avatarUrl) {
      return <img alt={`${item.firstName} ${item.lastName}`} className={styles.avatar} src={item.avatarUrl} />
    }
    const initials = `${item.firstName[0]}${item.lastName[0]}`.toUpperCase()
    return <div className={styles.avatarInitials}>{initials}</div>
  }

  const renderNotificationItem = item => {
    const fullName = `${item.firstName} ${item.lastName}`
    const message = (item.type === 'reciprocal' ? t.reciprocalMessage : t.socialOverlapMessage).replace(
      '%{name}',
      fullName,
    )
    const itemKey = item.connectionId != null ? `reciprocal-${item.connectionId}` : `overlap-${item.id}`
    return (
      <li key={itemKey} className={styles.item}>
        <div className={styles.avatarWrapper}>{renderAvatar(item)}</div>
        <div className={styles.content}>
          <p className={styles.name}>{fullName}</p>
          <p className={styles.message}>{message}</p>
        </div>
        <div className={styles.itemActions}>
          {item.type === 'reciprocal' && (
            <button className={styles.addButton} onClick={() => handleAddBack(item)} type="button">
              {t.addThemBack}
            </button>
          )}
          {item.type === 'reciprocal' ? (
            // Reciprocal dismiss: server-side via POST /notifications/working_circle/dismiss
            <button
              aria-label={t.dismiss}
              className={styles.dismissButton}
              disabled={isDismissing}
              onClick={() => handleDismiss(item.connectionId)}
              type="button"
            >
              ×
            </button>
          ) : (
            // Social overlap dismiss: localStorage only (Phase 1)
            <button
              aria-label={t.dismiss}
              className={styles.dismissButton}
              onClick={() => handleDismissOverlap(item.id)}
              type="button"
            >
              ×
            </button>
          )}
        </div>
      </li>
    )
  }

  return (
    <div className={styles.dropdown} ref={dropdownRef} role="dialog" aria-label={t.title}>
      <div className={styles.header}>
        <span className={styles.title}>{t.title}</span>
      </div>

      {visibleItems.length === 0 ? (
        <p className={styles.empty}>{t.empty}</p>
      ) : (
        <ul className={styles.list}>{visibleItems.map(renderNotificationItem)}</ul>
      )}
    </div>
  )
}

WorkingCircleNotificationDropdown.propTypes = {
  dispatch: PropTypes.func.isRequired,
  i18n: I18N.isRequired,
  isDismissing: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  notificationActions: ACTIONS.isRequired,
  onClose: PropTypes.func.isRequired,
  profileId: PropTypes.number.isRequired,
}

const mapStateToProps = state => ({
  isDismissing: selectIsDismissing(state),
  items: selectWorkingCircleNotifications(state),
  profileId: sessionSelectors.currentProfile(state).id,
})

const mapDispatchToProps = dispatch => ({
  dispatch,
  notificationActions: bindActionCreators({ fetchDismissNotification, fetchAddWorkingCircleMember }, dispatch),
})

export { WorkingCircleNotificationDropdown }

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Translation(WorkingCircleNotificationDropdown, ['workingCircleNotificationDropdown']))
